// src/components/sections/EnhancementSection.tsx
import React from 'react';
import { Settings, Inbox, ListChecks, TrendingUp, Sparkles, Edit2 } from 'lucide-react'; 
import { AgentData_V10, EnhancementSuggestionV10, TAgentDevelopmentStageV10, RoadmapItemPublicV10 } from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { IconButton } from '@dopomogai/ui';
import { Tooltip } from '@dopomogai/ui';

interface EnhancementSectionProps {
  agentData: AgentData_V10;
  onAction: (actionType: string, payload?: any) => void;
  id?: string;
  headerBgColor?: string;
}

const developmentStages: TAgentDevelopmentStageV10[] = [
  'Conceptual Idea', 'Proof of Concept (POC)', 'Minimum Viable Product (MVP)',
  'Version 1.0 (Launched)', 'Version 1.1', 'Version 2.0', 'Mature & Supported'
];
const getProgressPercentage = (stage: TAgentDevelopmentStageV10): number => {
  const currentIndex = developmentStages.indexOf(stage);
  if (currentIndex === -1) return 0;
  return Math.round(((currentIndex + 1) / developmentStages.length) * 100);
};

export const EnhancementSection = React.forwardRef<HTMLDivElement, EnhancementSectionProps>(
  ({ agentData, onAction, id, headerBgColor }, ref) => {
    const { developmentUpdatesSection: developmentSection, ownerConversationStats: conversationStats } = agentData;

    // --- FIX: Added a guard clause. If there's no development section at all, we render nothing.
    // This prevents all the "possibly 'undefined'" errors below.
    if (!developmentSection) {
      return null;
    }
    
    const selfEvolvingConfig = developmentSection?.selfEvolvingFunctionConfig;
    const recentSuggestions = selfEvolvingConfig?.recentSuggestions || [];
    const progressPercentage = getProgressPercentage(developmentSection.currentDevelopmentStage);

    return (
      <Card
        id={id} ref={ref}
        title="Agent Development & Enhancement"
        icon={<Sparkles size={22} className="text-gray-700" />}
        headerBgColor={headerBgColor}
        actionButton={
          <Tooltip text="Manage Enhancement Settings">
            <IconButton icon={<Settings size={16} />} ariaLabel="Manage enhancement settings" onClick={() => onAction('MANAGE_ENHANCEMENTS_SETTINGS', { url: selfEvolvingConfig?.viewAllSuggestionsUrl || '#' })} variant="outline" size="sm" />
          </Tooltip>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">Development Progress</h3>
              <Tooltip text={`Current stage: ${developmentSection.currentDevelopmentStage}`}>
                <div className="mb-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{developmentSection.currentDevelopmentStage}</span>
                    <span className="text-sm font-medium text-teal-600">{progressPercentage}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2.5 border border-black shadow-inner overflow-hidden">
                    <div className="bg-gradient-to-r from-teal-400 to-emerald-500 h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${progressPercentage}%` }} />
                  </div>
                </div>
              </Tooltip>
            </div>
            {selfEvolvingConfig && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">AI Enhancement Suggestions</h3>
                {selfEvolvingConfig.enhancementAgentStatus && (<p className="text-xs text-slate-500 mb-3 italic">Enhancement Agent Status: {selfEvolvingConfig.enhancementAgentStatus}</p>)}
                {recentSuggestions.length > 0 ? (
                  <div className="space-y-4">
                    {recentSuggestions.slice(0, 2).map((suggestion: EnhancementSuggestionV10) => (
                      <div key={suggestion.id} className="p-4 bg-slate-50 rounded-xl border-2 border-black shadow-[2px_2px_0_0_black]">
                        <div className="flex justify-between items-start mb-1">
                          <h4 className="font-semibold text-gray-800 text-md">{suggestion.title}</h4>
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full border border-blue-300 whitespace-nowrap">{suggestion.category}</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1 mb-3 leading-relaxed">{suggestion.description}</p>
                        {suggestion.estimatedImpact && (<p className="text-xs text-slate-500 mb-3">Estimated Impact: <span className="font-medium">{suggestion.estimatedImpact}</span></p>)}
                        <Button text={suggestion.suggestedAction.text} variant={suggestion.suggestedAction.styleType === 'primary_blue' ? 'primary' : 'secondary'} size="sm" icon={suggestion.suggestedAction.icon} onClick={() => onAction(suggestion.suggestedAction.actionType, suggestion.suggestedAction.actionPayload)} />
                      </div>
                    ))}
                    {selfEvolvingConfig.viewAllSuggestionsUrl && recentSuggestions.length > 0 && (
                      <div className="text-right">
                        <Button text="View All Suggestions" variant="ghost" size="sm" onClick={() => onAction('VIEW_ALL_ENHANCEMENT_SUGGESTIONS', { url: selfEvolvingConfig.viewAllSuggestionsUrl })} className="text-teal-600 hover:text-teal-700" />
                      </div>
                    )}
                  </div>
                ) : (<p className="text-slate-500 italic text-sm">No AI-driven enhancement suggestions at this time.</p>)}
              </div>
            )}
          </div>
          <div className="space-y-6">
            {developmentSection.publicRoadmap?.items && developmentSection.publicRoadmap.items.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Roadmap Highlights</h3>
                <div className="space-y-3">
                  {developmentSection.publicRoadmap.items.slice(0, 2).map((item: RoadmapItemPublicV10) => (
                    <div key={item.id} className="p-3 bg-slate-50 rounded-lg border-2 border-black shadow-[1px_1px_0_0_black]">
                      <div className="flex justify-between items-start">
                        <h4 className="font-medium text-gray-800 text-sm">{item.title}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${ item.status === 'In Progress' ? 'bg-blue-100 text-blue-800 border-blue-300' : item.status === 'Planned' ? 'bg-purple-100 text-purple-800 border-purple-300' : item.status === 'Completed & Launched' ? 'bg-green-100 text-green-800 border-green-300' : 'bg-slate-100 text-slate-800 border-slate-300' }`}>{item.status}</span>
                      </div>
                      {item.description && (<p className="text-xs text-slate-600 mt-1.5">{item.description}</p>)}
                      {item.eta && (<p className="text-xs text-slate-500 mt-1.5">Target ETA: {item.eta}</p>)}
                    </div>
                  ))}
                  {developmentSection.publicRoadmap.fullRoadmapUrl && (
                     <div className="text-right">
                        <Button text="View Full Roadmap" variant="ghost" size="sm" onClick={() => onAction('VIEW_FULL_ROADMAP_OWNER', { url: developmentSection.publicRoadmap?.fullRoadmapUrl })} className="text-teal-600 hover:text-teal-700" />
                    </div>
                  )}
                </div>
              </div>
            )}
            {conversationStats && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Conversations Overview</h3>
                <div className="bg-slate-100 rounded-xl border-2 border-black p-5 shadow-[2px_2px_0_0_black]">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-5 mb-5">
                    {[
                      { label: "New Today", value: conversationStats.newToday, icon: <Sparkles size={16} className="text-emerald-600" /> },
                      { label: "Unreviewed", value: conversationStats.unreviewed, icon: <Inbox size={16} className="text-amber-600" /> },
                      { label: "Total (30d)", value: conversationStats.totalLast30Days, icon: <ListChecks size={16} className="text-blue-600" /> },
                      { label: "Avg. Session", value: conversationStats.avgSessionLength || "N/A", icon: <TrendingUp size={16} className="text-purple-600" /> },
                    ].map(stat => (
                      <Tooltip key={stat.label} text={`${stat.label}: ${stat.value}`}>
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-white rounded-md border border-slate-300 shadow-sm">{stat.icon}</div>
                            <div>
                                <p className="text-xs text-slate-500 uppercase tracking-wider">{stat.label}</p>
                                <p className="text-xl font-semibold text-gray-800">{stat.value}</p>
                            </div>
                        </div>
                      </Tooltip>
                    ))}
                  </div>
                  <Button text="View Conversations" icon={<Inbox size={16} />} variant="secondary" onClick={() => onAction('VIEW_CONVERSATION_INBOX', null)} fullWidth />
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  }
);
EnhancementSection.displayName = 'EnhancementSection';